import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/data";
import { CONFIG } from "@/lib/config";
import { XLogo, Mono } from "@/components/bits";

const RESOURCES = [
    { label: "Arc", href: "https://www.circle.com/arc" },
    { label: "Circle", href: "https://www.circle.com" },
    { label: "Argus", href: "https://argus.world" },
    { label: "DEXScreener", href: "https://dexscreener.com" },
];

const LEGAL = [
    { label: "Risk Disclosure", to: "/risk" },
    { label: "Terms", to: "/legal#terms" },
    { label: "Privacy", to: "/legal#privacy" },
];

export default function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer className="relative border-t border-white/10 bg-vera-bg2/50" data-testid="vera-footer">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-10">
                <div className="grid grid-cols-2 md:grid-cols-12 gap-10 mb-14">
                    <div className="col-span-2 md:col-span-5">
                        <div className="flex items-center gap-3 mb-5">
                            <img src={CONFIG.LOGO} alt="VERA logo" className="h-12 w-12 object-contain mask-fade-circle" />
                            <div>
                                <p className="font-black tracking-[0.35em] text-sm">VERA</p>
                                <p className="font-mono text-[9px] tracking-[0.25em] text-vera-mut mt-1">VERIFIED REAL ASSETS</p>
                            </div>
                        </div>
                        <p className="text-vera-mut text-sm leading-relaxed max-w-xs mb-2">REAL ASSETS. PROGRAMMABLE CAPITAL.</p>
                        <p className="text-vera-mut/60 text-xs leading-relaxed max-w-xs">Arc-native infrastructure for tokenized real-world value, settled in USDC.</p>
                        <a
                            href={CONFIG.X_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-x-link"
                            className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] border border-white/15 px-4 py-2.5 text-vera-mut hover:text-white hover:border-vera-soft/60 transition-all"
                        >
                            <XLogo className="w-3.5 h-3.5" /> X
                        </a>
                    </div>

                    <div className="md:col-span-3">
                        <Mono className="text-vera-gold block mb-5">NAVIGATION</Mono>
                        <ul className="space-y-3">
                            {NAV_LINKS.map((l) => (
                                <li key={l.label}>
                                    <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase()}`} className="text-sm text-vera-mut hover:text-white transition-colors">
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <Mono className="text-vera-gold block mb-5">RESOURCES</Mono>
                        <ul className="space-y-3">
                            {RESOURCES.map((r) => (
                                <li key={r.label}>
                                    <a href={r.href} target="_blank" rel="noopener noreferrer" data-testid={`footer-resource-${r.label.toLowerCase()}`} className="inline-flex items-center gap-1.5 text-sm text-vera-mut hover:text-white transition-colors">
                                        {r.label}
                                        <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <Mono className="text-vera-gold block mb-5">LEGAL</Mono>
                        <ul className="space-y-3">
                            {LEGAL.map((l) => (
                                <li key={l.label}>
                                    <Link to={l.to} data-testid={`footer-legal-${l.label.toLowerCase()}`} className="text-sm text-vera-mut hover:text-white transition-colors">
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        <Mono className="text-vera-mut block mt-8 mb-3">SOCIAL</Mono>
                        <p className="text-xs text-vera-mut/70 leading-relaxed">X only: @VeraOnArc. No other accounts exist.</p>
                    </div>
                </div>

                <div className="hairline mb-8" aria-hidden="true" />
                <p className="text-[11px] text-vera-mut/60 leading-relaxed max-w-3xl mb-5">
                    VERA is infrastructure under development. Nothing on this website is an offer, solicitation or investment advice. No returns are promised, assets are not risk-free, and token ownership does not imply legal ownership of any real-world asset. All example data is labeled DEMO or EXAMPLE.
                </p>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <p className="font-mono text-[10px] tracking-[0.2em] text-vera-mut/60" data-testid="footer-copyright">
                        © {year} VERA — VERIFIED REAL ASSETS
                    </p>
                    <p className="font-mono text-[10px] tracking-[0.2em] text-vera-mut/60">
                        BUILT FOR ARC <span className="text-vera-gold mx-2">◆</span> USDC SETTLEMENT
                    </p>
                </div>
            </div>
        </footer>
    );
}
